import { INGREDIENT_SERVING_STATES } from "./ingredientServingState";

const SEGMENT_KEY_PREFIX = "seg_";

/**
 * Numeric sort value from `segment.position` only. Missing/invalid positions sort last (not derived from segment keys).
 */
function segmentPositionSortValue(segment) {
	let position = segment?.position;
	if(isNaN(position)) return null;
	position = parseInt(position);
	return position;
}

/**
 * Sort [key, segment] by `segment.position` only (invalid/missing last). Tie-break by key for stability.
 * @param {Object} segments
 * @returns {Array<[string, object]>}
 */
export function sortSegmentsEntriesByPosition(segments) {
	return Object.entries(segments || {}).sort(([keyA, segA], [keyB, segB]) => {
		const va = segmentPositionSortValue(segA);
		const vb = segmentPositionSortValue(segB);
		if(va === null && vb === null) return 0;
		if(va === null) return 1;
		if(vb === null) return -1;
		return va - vb;
	});
}

export function getSortedSegmentKeys(segments) {
	return sortSegmentsEntriesByPosition(segments).map(([k]) => k);
}

/**
 * Rebuild segments: keys `seg_1..seg_n` and `position` 1..n in display order (insertion order matches position).
 * @param {Object} segments - Segments map (not mutated)
 * @returns {Object} New segments object
 */
export function reindexSegmentPositions(segments) {
	const entries = sortSegmentsEntriesByPosition(segments);
	const out = {};
	entries.forEach(([, seg], idx) => {
		const newKey = SEGMENT_KEY_PREFIX + (idx + 1);
		out[newKey] = { ...seg, position: idx + 1 };
	});
	return out;
}

/**
 * Formats preparation time from seconds into a human-readable string (e.g. "2 min 30 sec").
 * @param {number} totalSeconds - Preparation time in seconds
 * @returns {string} Beautified string or "-" if invalid/empty
 */
export function formatPreparationTime(totalSeconds) {
	if (totalSeconds == null || totalSeconds < 0 || Number.isNaN(Number(totalSeconds))) {
		return "-";
	}
	const seconds = Math.floor(Number(totalSeconds));
	if (seconds === 0) return "0 sec";
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	const parts = [];
	if (mins > 0) parts.push(`${mins} min`);
	if (secs > 0) parts.push(`${secs} sec`);
	return parts.join(" ") || "0 sec";
}

export function reorderIngredientsBasedOnPosition(ingredients) {
	return Object.entries(ingredients)
		.sort(([, { position: position1 }], [, { position: position2 }]) => position1 - position2)
		.reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
}

/**
 * Reorder segments by moving one from startIndex to endIndex (indices match sortSegmentsEntriesByPosition order).
 * Returns segments with keys `seg_1..seg_n` and contiguous `position` in display order.
 */
export function reorder(segments, startIndex, endIndex) {
	segments = segments || {};
	const keys = getSortedSegmentKeys(segments);
	const reordered = [...keys];
	const [removed] = reordered.splice(startIndex, 1);
	reordered.splice(endIndex, 0, removed);

	const staged = {};
	reordered.forEach((key, i) => {
		const seg = segments[key];
		staged[key] = { ...seg, position: i + 1 };
	});
	return reindexSegmentPositions(staged);
}

/**
 * Sort ingredients by their position property, handling edge cases
 *
 * @param {Object} ingredients - Object with ingredient IDs as keys
 * @returns {Array} Array of [ingredientId, ingredientObject] tuples sorted by position
 *
 * Sorting logic:
 * - Ingredients with valid numeric positions sorted numerically (1, 2, 3...)
 * - Ingredients with invalid/missing positions moved to end
 * - Handles both number and string position values
 */
export function sortIngredientsArrayByPosition(ingredients) {
	return Object.entries(ingredients || {}).sort(([, a], [, b]) => {
		// Convert positions to numbers, handling string and number types
		const pa = typeof a.position === "number" ? a.position : Number(a.position);
		const pb = typeof b.position === "number" ? b.position : Number(b.position);

		// Handle invalid positions: NaN ingredients go to end
		if (isNaN(pa) && isNaN(pb)) return 0; // Both invalid: maintain relative order
		if (isNaN(pa)) return 1; // a invalid: a goes after b
		if (isNaN(pb)) return -1; // b invalid: a goes before b

		// Both valid: numerical sort
		return pa - pb;
	});
}

/**
 * Reindex ingredient positions within a single segment (1-based numbering)
 *
 * @param {Object} mutIngredientsObj - Mutable ingredients object to modify in place
 *
 * Process:
 * 1. Sort ingredients by current position
 * 2. Reassign positions as 1, 2, 3... within the segment
 * 3. Modify object in place (mutation)
 *
 * Used after removing ingredients to eliminate gaps in numbering
 */
export function reindexIngredientPositions(mutIngredientsObj) {
	const ordered = sortIngredientsArrayByPosition(mutIngredientsObj);
	ordered.forEach(([id, obj], idx) => {
		// Assign 1-based position within this segment
		mutIngredientsObj[id] = { ...obj, position: idx + 1 };
	});
}

/**
 * Reindex ingredient positions globally across all segments
 *
 * This function ensures that ingredient positions are incremental across the entire recipe,
 * not just within individual segments. For example:
 * - Segment A ingredients: positions 1, 2, 3
 * - Segment B ingredients: positions 4, 5, 6 (not starting from 1)
 *
 * @param {Object} mutSegments - Mutable segments object to modify in place
 *
 * Flow:
 * 1. Start with position counter = 1
 * 2. Iterate through segments in their current order
 * 3. For each segment, get ingredients sorted by current position
 * 4. Reassign positions incrementally across all segments
 * 5. Continue counter across segment boundaries
 */
export function reindexAllIngredientPositionsGlobally(mutSegments) {
	let runningPosition = 1;
	const segmentKeysInOrder = getSortedSegmentKeys(mutSegments);
	for (const segKey of segmentKeysInOrder) {
		const ingredientsObj = mutSegments[segKey]?.ingredients || {};
		const ordered = sortIngredientsArrayByPosition(ingredientsObj);

		// Reassign positions incrementally, continuing from previous segment
		for (const tuple of ordered) {
			const ingId = tuple[0];
			const ingObj = tuple[1];
			ingredientsObj[ingId] = { ...ingObj, position: runningPosition };
			runningPosition += 1;
		}
	}
}

const SERVING_TEMP_ORDER = {
	[INGREDIENT_SERVING_STATES.HOT]: 0,
	[INGREDIENT_SERVING_STATES.COLD]: 1,
};

const order = (t) => SERVING_TEMP_ORDER[t] ?? -1;// Fallback to -1 if not found

/**
 * Validates serving temperature ordering (HOT before COLD). Throws on first violation.
 *
 * @param {string} targetServingTemp - INGREDIENT_SERVING_STATES.HOT | COLD
 * @param {Object} prevIngredient - Ingredient before target (can be null)
 * @param {Object} nextIngredient - Ingredient after target (can be null)
 * @throws {Error} When order is invalid, with message for first violation (previous then next)
 */
export function validateServingTemperatureOrder(targetServingTemp, prevIngredient, nextIngredient) {
	const prevTemp = prevIngredient?.servingTemp,
		nextTemp = nextIngredient?.servingTemp,
		isValidPrev = prevTemp in INGREDIENT_SERVING_STATES,
		isValidNext = nextTemp in INGREDIENT_SERVING_STATES,
		isValidTarget = targetServingTemp in INGREDIENT_SERVING_STATES;

	if (!isValidTarget) throw new Error(`Target ingredient serving state (${targetServingTemp}) is not a valid serving state`);
	if (isValidPrev && order(prevTemp) > order(targetServingTemp)) throw new Error(`Previous ingredient (${prevIngredient?.name || ""}) has higher serving temperature`);
	if (isValidNext && order(nextTemp) < order(targetServingTemp)) throw new Error(`Next ingredient (${nextIngredient?.name || ""}) has lower serving temperature`);
}

/**
 * Validates that the global ingredient order across segments obeys serving temperature (HOT before COLD).
 * Used to reject segment reorders that would violate the rule.
 *
 * @param {Object} segments - Segments object (e.g. result of reorder()) in display order
 * @throws {Error} When order is invalid, with message for first violation
 */
export function validateSegmentsServingTemperatureOrder(segments) {
	segments = segments || {};
	const segmentKeys = getSortedSegmentKeys(segments);
	const globalIngredients = [];
	for (const segKey of segmentKeys) {
		const ingredientsObj = segments[segKey]?.ingredients || {};
		const ordered = sortIngredientsArrayByPosition(ingredientsObj);
		ordered.forEach(([, obj]) => globalIngredients.push(obj));
	}
	for (let i = 0; i < globalIngredients.length - 1; i++) {
		const prev = globalIngredients[i];
		const next = globalIngredients[i + 1];
		const prevTemp = prev?.servingTemp;
		const nextTemp = next?.servingTemp;
		const isValidPrev = prevTemp in INGREDIENT_SERVING_STATES;
		const isValidNext = nextTemp in INGREDIENT_SERVING_STATES;
		if (isValidPrev && isValidNext && order(prevTemp) > order(nextTemp)) {
			throw new Error(
				`Serving temperature order would be violated: "${prev?.name || "Ingredient"}" (${prevTemp}) cannot come before "${next?.name || "Ingredient"}" (${nextTemp}).`
			);
		}
	}
}
